const mountedHeroes=new WeakMap();
const SAMPLE_ROOT = 'https://pb-media-worker.perfectpresents.workers.dev/gift/';
/** Same sample content previously played inside the phone, using the same player. */
export function originalDemoGift() {
  return {format:'gift-cards',version:1,theme:'cherry',to:'Maya',from:'Alex',cards:[
    {id:'sample-code',type:'lock',skin:'soft',question:'Enter any 4 digits',answer:'0000',demoAnyCode:true},
    {id:'sample-photo',type:'puzzle',skin:'soft',title:'Our favorite photo',src:SAMPLE_ROOT+'cards/thumbnails/couple-demo.jpg'},
    {id:'sample-note',type:'message',skin:'soft',message:'Happy birthday, Maya!\n\nFor every little thing you do that makes my world better. I wish I could be there today. Until then, here is a little surprise, chosen just for you.\n\nAlways, Alex ♡'},
    {id:'sample-voice',type:'audio',skin:'soft',title:'A little voice message',src:SAMPLE_ROOT+'demo/message.m4a',audioSkin:'tape'},
    {id:'sample-spin',type:'spin',skin:'soft',title:'One more surprise',prizes:[{id:'coffee',label:'Unli Coffee'},{id:'plane',label:'Trip to Boracay'},{id:'house',label:'House and Lot'}]}
  ]};
}

/** Fence iframe readiness to its window, origin and current preparation request. */
export function createDemoSession({frame,win,player,loadPlayer,product,onState,setTimer=setTimeout,clearTimer=clearTimeout}) {
  let playerDocument=player;
  let state='idle',loaded=false,ready=false,requestId='',sentId='',sequence=0,timer;
  const send=data=>frame.contentWindow?.postMessage(data,win.location.origin);
  const change=value=>{state=value;onState(value);};
  const clear=()=>{clearTimer(timer);timer=undefined;};
  const fail=()=>{clear();loaded=false;ready=false;requestId='';send({type:'cards:pause'});change('failed');};
  function prepare(){
    if(state!=='loading'||!ready||sentId===requestId)return;
    sentId=requestId;send({type:'cards:preview',id:requestId,gift:originalDemoGift(),product});
  }
  function open(){
    if(state==='loading'||state==='playing')return;
    if(!playerDocument&&!loadPlayer){fail();return;}
    requestId='frameless-demo-'+ ++sequence;const opening=requestId;change('loading');timer=setTimer(fail,20000);
    const mount=html=>{if(state!=='loading'||requestId!==opening)return;if(typeof html!=='string'||!html.includes('</body>')){fail();return;}playerDocument=html;if(!loaded){loaded=true;frame.srcdoc=html;}else prepare();};
    if(playerDocument)mount(playerDocument);else Promise.resolve().then(loadPlayer).then(mount).catch(()=>{if(state==='loading'&&requestId===opening)fail();});
  }
  function close(){clear();requestId='';send({type:'cards:pause'});change('idle');}
  function message(event){
    if(event.origin!==win.location.origin||event.source!==frame.contentWindow)return;
    if(event.data?.type==='cards:ready'){ready=true;prepare();return;}
    if(event.data?.type==='ip:demo-close'){close();return;}
    if(event.data?.type!=='cards:prepared'||state!=='loading'||event.data.id!==requestId)return;
    if(event.data.error){fail();return;}
    clear();send({type:'cards:resume'});change('playing');
  }
  const pause=()=>send({type:'cards:pause'});
  const resume=()=>{if(state==='playing')send({type:'cards:resume'});};
  frame.addEventListener('load',()=>{if(loaded)send({type:'cards:hello'});});
  win.addEventListener('message',message);
  win.addEventListener('pagehide',pause);
  win.addEventListener('pageshow',resume);
  const visibility=()=>win.document.hidden?pause():resume();
  win.document.addEventListener('visibilitychange',visibility);
  return {open,close,state:()=>state,dispose(){close();win.removeEventListener?.('message',message);win.removeEventListener?.('pagehide',pause);win.removeEventListener?.('pageshow',resume);win.document.removeEventListener?.('visibilitychange',visibility);}};
}

/** Fit the upright frame into the first fold without scaling text or touch targets. */
export function demoWidth({viewportWidth,viewportHeight,heroTop,copyHeight,actionsHeight,chromeHeight,columnWidth}){
  if(viewportWidth>700)return Math.min(360,columnWidth);
  const available=viewportHeight-heroTop-copyHeight-actionsHeight-chromeHeight;
  return Math.min(320,columnWidth,Math.max(240,available*.75));
}

export function createGalleryClock({advance,canRun,now=Date.now,setTimer=setTimeout,clearTimer=clearTimeout,interval=3000,idle=8000}){
 let timer,next=now()+interval,heldUntil=0,running=false,disposed=false,blocked=false;
 const clear=()=>{clearTimer(timer);timer=undefined;};
 async function tick(){
  timer=undefined;if(disposed||running||!canRun())return;
  running=true;next=now()+interval;
  try{await advance();}catch{disposed=true;}
  running=false;refresh();
 }
 function refresh(){
  clear();if(disposed)return;
  if(!canRun()){blocked=true;return;}
  if(blocked){next=Math.max(now()+interval,heldUntil);blocked=false;}
  if(!running)timer=setTimer(tick,Math.max(0,next-now()));
 }
 return {refresh,interact(){heldUntil=now()+idle;next=heldUntil;refresh();},dispose(){disposed=true;clear();}};
}

export function mountHeroGallery(hero,doc,win,dialog){
 const deck=hero.querySelector('.gallery-deck'),touch=deck.querySelector('.gallery-touch'),cards=[...deck.querySelectorAll('.gallery-card')],status=hero.querySelector('.gallery-status');
 const reduced=win.matchMedia('(prefers-reduced-motion: reduce)'),animations=new Set(),nativeTouch='ontouchstart' in win;
 const slots=['translate(0px,0px) rotate(0deg) scale(1)','translate(12px,12px) rotate(6deg) scale(.97)','translate(-8px,25px) rotate(-5deg) scale(.93)','translate(-8px,28px) rotate(-5deg) scale(.91)'];
 let order=cards.map((_,i)=>i),busy=false,drag=null,suppressClick=0,keyboard=false,hover=false,visible=true,disposed=false,pageActive=true,pending=0,paintFrame=0,pointerFocus=false,motionEpoch=0;
 function place(){
  order.forEach((id,depth)=>{const card=cards[id];card.hidden=depth>2;card.dataset.depth=depth;card.style.transform=slots[Math.min(depth,3)];card.style.zIndex=String(cards.length-depth);card.classList.remove('is-arriving');card.inert=depth!==0;card.setAttribute('aria-hidden',String(depth!==0));});
  deck.dataset.activeCard=order[0];deck.dataset.busy='false';deck.setAttribute('aria-label',cards[order[0]].dataset.label);
  for(const id of [order[1],order.at(-1)])cards[id].querySelectorAll('img').forEach(image=>{image.loading='eager';});
 }
 const animate=(card,from,to,duration,delay=0)=>{
  const animation=card.animate([{transform:from},{transform:to}],{duration,delay,easing:'cubic-bezier(.22,.7,.22,1)',fill:'forwards'});animations.add(animation);return animation.finished.catch(()=>{});
 };
 async function advance(direction=1,fromDrag=false,manualMotion=false){
  if(busy||disposed)return false;busy=true;deck.dataset.busy='true';
  const epoch=motionEpoch,before=order.slice(),front=cards[before[0]],duration=reduced.matches||!front.animate?0:manualMotion?320:720;
  try{
   if(duration){
    const tasks=[];
    if(direction>0){
     cards[before[1]].classList.add('is-arriving');
     // Only the three visible cards need motion; the hidden fourth has no visual role.
     for(let depth=1;depth<3;depth++)tasks.push(animate(cards[before[depth]],slots[depth],slots[depth-1],duration*.82,duration*.18));
     const outside='translate(-58%,-8%) rotate(-16deg) scale(1.01)';
     await animate(front,fromDrag?front.style.transform:slots[0],outside,duration*.45);if(disposed||epoch!==motionEpoch)return false;
     front.style.zIndex='0';await animate(front,outside,slots[3],duration*.55);
    }else{
     const incoming=cards[before.at(-1)],outside='translate(58%,-8%) rotate(16deg) scale(1.01)';incoming.hidden=false;incoming.classList.add('is-arriving');incoming.style.zIndex=String(cards.length+1);
     for(let depth=0;depth<3;depth++)tasks.push(animate(cards[before[depth]],fromDrag&&depth===0?front.style.transform:slots[depth],slots[depth+1],duration));
     await animate(incoming,outside,slots[0],duration);
    }
    await Promise.all(tasks);
   }
   if(disposed||epoch!==motionEpoch)return false;
   order=direction>0?[...before.slice(1),before[0]]:[before.at(-1),...before.slice(0,-1)];
   return true;
  }finally{
   animations.forEach(animation=>animation.cancel());animations.clear();busy=false;if(!disposed)place();if(!manualMotion&&pending){const next=pending;pending=0;if(pageActive&&!doc.hidden&&!dialog.open&&visible)void manual(next);}
  }
 }
 const clock=createGalleryClock({advance,canRun:()=>!disposed&&pageActive&&!doc.hidden&&!dialog.open&&!reduced.matches&&visible&&!keyboard&&!hover&&!drag});
 // One pending action absorbs quick repeat taps without building a long animation queue.
 async function manual(direction=1,fromDrag=false){
  clock.interact();if(busy){pending=direction;return;}
  try{if(await advance(direction,fromDrag,true))status.textContent=cards[order[0]].dataset.label;}catch{}
  const next=pending;pending=0;
  if(next&&pageActive&&!doc.hidden&&!dialog.open&&visible)await manual(next);
  clock.refresh();
 }
 const requestFrame=win.requestAnimationFrame?.bind(win)||((fn)=>setTimeout(fn,16));
 const cancelFrame=win.cancelAnimationFrame?.bind(win)||clearTimeout;
 function paintDrag(){
  paintFrame=0;if(!drag?.engaged||drag.blocked||reduced.matches)return;
  const dx=Math.max(-180,Math.min(180,drag.dx));
  drag.card.style.transform=`translate(${dx}px,${-Math.abs(dx)*.035}px) rotate(${dx*.055}deg) scale(1)`;
 }
 function flushDrag(){if(paintFrame){cancelFrame(paintFrame);paintFrame=0;}paintDrag();}
 function settleDrag(card){
  if(!reduced.matches&&card.animate){const animation=card.animate([{transform:card.style.transform},{transform:slots[0]}],{duration:160,easing:'ease-out'});animations.add(animation);animation.finished.catch(()=>{}).finally(()=>{animations.delete(animation);animation.cancel()});}
  card.style.transform=slots[0];
 }
 function release(current){if(current.kind==='touch')return;try{if(deck.hasPointerCapture?.(current.id))deck.releasePointerCapture(current.id);}catch{}}
 function cancelDrag(event){
  if(!drag||event?.pointerId!==undefined&&event.pointerId!==drag.id)return;
  flushDrag();const current=drag;drag=null;deck.dataset.dragging='false';release(current);
  if(current.engaged){suppressClick=Date.now()+400;if(!current.blocked)settleDrag(current.card);}clock.interact();
 }
 touch.addEventListener('click',()=>{if(Date.now()>=suppressClick)manual();});
 touch.addEventListener('focus',()=>{keyboard=touch.matches?touch.matches(':focus-visible'):!pointerFocus;clock.refresh();});
 touch.addEventListener('keydown',event=>{keyboard=true;clock.refresh();if(['ArrowLeft','ArrowRight'].includes(event.key)){event.preventDefault();manual(event.key==='ArrowLeft'?-1:1);}});
 touch.addEventListener('blur',()=>{keyboard=false;pointerFocus=false;clock.interact();});
 deck.addEventListener('pointerenter',event=>{if(event.pointerType==='mouse'){hover=true;clock.refresh();}});
 deck.addEventListener('pointerleave',event=>{hover=false;if(event.pointerType==='mouse'&&drag&&!drag.engaged)cancelDrag(event);clock.refresh();});
 function startDrag(event,kind='pointer'){
  if(event.button>0||event.isPrimary===false||drag)return;
  pointerFocus=true;keyboard=false;clock.interact();if(busy&&kind!=='touch')return;
  // Retire an unfinished snap-back before accepting another gesture.
  if(!busy){animations.forEach(animation=>animation.cancel());animations.clear();}
  drag={kind,blocked:busy,id:event.pointerId,x:event.clientX,y:event.clientY,dx:0,start:event.timeStamp,card:cards[order[0]],engaged:false};
 }
 function moveDrag(event){
  if(!drag||drag.id!==event.pointerId)return;
  const dx=event.clientX-drag.x,dy=event.clientY-drag.y;
  // Keep ambiguous diagonals undecided; vertical intent belongs to page scrolling.
  if(!drag.engaged&&Math.abs(dy)>8&&Math.abs(dy)>Math.abs(dx)*1.15){suppressClick=Date.now()+400;cancelDrag(event);return;}
  if(!drag.engaged&&Math.abs(dx)>8&&Math.abs(dx)>Math.abs(dy)*1.15){
   drag.engaged=true;deck.dataset.dragging='true';try{deck.setPointerCapture?.(event.pointerId);}catch{cancelDrag(event);return;}
  }
  if(drag.engaged){drag.dx=dx;if(!paintFrame)paintFrame=requestFrame(paintDrag);}
 }
 function endDrag(event){
  if(!drag||drag.id!==event.pointerId)return;
  drag.dx=event.clientX-drag.x;flushDrag();
  const current=drag,dx=current.dx,elapsed=event.timeStamp-current.start;drag=null;deck.dataset.dragging='false';release(current);
  if(current.engaged||Math.hypot(dx,event.clientY-current.y)>12)suppressClick=Date.now()+400;
  const threshold=Math.min(60,(deck.getBoundingClientRect?.().width||280)*.18);
  const flick=elapsed>0&&elapsed<300&&Math.abs(dx)>=20&&Math.abs(dx)/elapsed>.45;
  clock.interact();if(current.engaged&&(Math.abs(dx)>threshold||flick))manual(dx<0?1:-1,!current.blocked);else if(current.engaged&&!current.blocked)settleDrag(current.card);
 }
 // Touch Events own finger gestures: cancelling pointermove cannot stop native
 // scrolling. Decide on the first nonzero touchmove, before scrolling commits.
 const touchPoint=(point,event)=>({pointerId:point.identifier,clientX:point.clientX,clientY:point.clientY,timeStamp:event.timeStamp});
 if(nativeTouch){
  deck.addEventListener('touchstart',event=>{
   if(event.touches.length!==1){suppressClick=Date.now()+400;cancelDrag();return;}
   startDrag(touchPoint(event.touches[0],event),'touch');
  },{passive:true});
  deck.addEventListener('touchmove',event=>{
   if(!drag||drag.kind!=='touch')return;
   if(event.touches.length!==1){cancelDrag();return;}
   const point=event.touches[0];if(point.identifier!==drag.id)return;
   const dx=point.clientX-drag.x,dy=point.clientY-drag.y;
   if(!dx&&!dy)return;
   if(!drag.engaged){
    // Once yielded vertically, this finger cannot steal the page scroll later.
    if(Math.abs(dy)>=Math.abs(dx)||!event.cancelable){suppressClick=Date.now()+400;cancelDrag();return;}
    drag.engaged=true;deck.dataset.dragging='true';
   }
   if(!event.cancelable){cancelDrag();return;}
   event.preventDefault(); // Keep sideways ownership through subsequent diagonal drift.
   drag.dx=dx;if(!paintFrame)paintFrame=requestFrame(paintDrag);
  },{passive:false});
  deck.addEventListener('touchend',event=>{
   if(!drag||drag.kind!=='touch')return;
   const point=[...event.changedTouches].find(point=>point.identifier===drag.id);
   if(point)endDrag(touchPoint(point,event));
  },{passive:true});
  deck.addEventListener('touchcancel',()=>{if(drag?.kind==='touch')cancelDrag();},{passive:true});
 }
 const pointerOnly=handler=>event=>{if(nativeTouch&&event.pointerType==='touch')return;handler(event);};
 deck.addEventListener('pointerdown',pointerOnly(startDrag));
 deck.addEventListener('pointermove',pointerOnly(moveDrag));
 deck.addEventListener('pointerup',pointerOnly(endDrag));
 deck.addEventListener('pointercancel',pointerOnly(cancelDrag));
 deck.addEventListener('lostpointercapture',event=>{
  // Implicit capture can move from the overlay button to this deck. Its lost event
  // bubbles here, but only losing the deck's own capture cancels our gesture.
  if(drag?.kind!=='touch'&&(!event.target||event.target===deck))cancelDrag(event);
 });
 function interruptMotion(){pending=0;motionEpoch++;cancelDrag();animations.forEach(animation=>animation.cancel());animations.clear();}
 const refresh=()=>{if(doc.hidden||dialog.open||!visible||reduced.matches)interruptMotion();clock.refresh();};
 doc.addEventListener('visibilitychange',refresh);reduced.addEventListener?.('change',refresh);
 const observer=win.IntersectionObserver?new win.IntersectionObserver(entries=>{visible=entries[0]?.isIntersecting!==false;refresh();}):null;observer?.observe(deck);
 const hide=()=>{pageActive=false;interruptMotion();clock.refresh();},show=()=>{pageActive=true;clock.refresh();};
 win.addEventListener('pagehide',hide);win.addEventListener('pageshow',show);
 place();clock.refresh();return {refresh,interact:()=>clock.interact(),advance:manual,dispose(){disposed=true;clock.dispose();interruptMotion();observer?.disconnect();doc.removeEventListener?.('visibilitychange',refresh);reduced.removeEventListener?.('change',refresh);win.removeEventListener?.('pagehide',hide);win.removeEventListener?.('pageshow',show);}};
}

export function mountFramelessHero(doc=document,win=window,hero=doc.querySelector('.ip-frameless-hero')){
 if(!hero)return;
 if(mountedHeroes.has(hero))return mountedHeroes.get(hero);
 const dialog=hero.querySelector('.demo-dialog'),chat=hero.querySelector('.chat'),frame=hero.querySelector('.actual-demo'),gift=hero.querySelector('[data-open-demo]'),label=gift.querySelector('.gift-open');
 const playerStyle=hero.dataset.playerStyle;
 const bridge=`<script>document.addEventListener('keydown',event=>{if(event.key==='Escape'){event.preventDefault();parent.postMessage({type:'ip:demo-close'},${JSON.stringify(win.location.origin)});}});<\/script>`;
 const decorate=html=>html?.replace('</body>',bridge+'</body>').replace('</head>',(playerStyle?`<link rel="stylesheet" href="${playerStyle.replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;')}">`:'')+'</head>');
 let opener,overflow='';
 const gallery=mountHeroGallery(hero,doc,win,dialog);
 const player=decorate(win.IP_PREVIEW?.player);
 const session=createDemoSession({frame,win,player,loadPlayer:async()=>{const response=await win.fetch(hero.dataset.demoUrl,{signal:win.AbortSignal.timeout(15000)});if(!response.ok)throw Error('Demo unavailable');return decorate(await response.text());},product:{title:'Monogram Necklace · sample gift',image:hero.dataset.sampleImage||win.IP_PREVIEW?.necklace},onState(state){
  const playing=state==='playing';chat.hidden=playing;frame.hidden=!playing;gift.setAttribute('aria-busy',String(state==='loading'));
  label.textContent=state==='loading'?'Opening…':state==='failed'?'Couldn’t open. Tap to retry →':'Tap to open →';
  if(playing&&dialog.open)frame.focus({preventScroll:true});
  if(state==='idle'&&dialog.open)dialog.close();
 }});
 gift.addEventListener('click',session.open);
 hero.querySelectorAll('.how').forEach(button=>button.addEventListener('click',()=>{if(dialog.open)return;opener=button;overflow=doc.body.style.overflow;doc.body.style.overflow='hidden';dialog.showModal();gallery.refresh();hero.querySelector('.demo-close').focus({preventScroll:true});}));
 hero.querySelector('.demo-close').addEventListener('click',()=>dialog.close());
 dialog.addEventListener('click',event=>{if(event.target===dialog){const r=dialog.getBoundingClientRect();if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)dialog.close();}});
 dialog.addEventListener('close',()=>{session.close();doc.body.style.overflow=overflow;gallery.interact();opener?.focus({preventScroll:true});});
 const stopFit=win.ResizeObserver?fitHero(hero,win):null,stopSession=session.dispose;
 session.dispose=()=>{stopSession();gallery.dispose();stopFit?.();mountedHeroes.delete(hero);};
 mountedHeroes.set(hero,session);return session;
}
if(typeof document!=='undefined'){document.querySelectorAll('.ip-frameless-hero').forEach(hero=>mountFramelessHero(document,window,hero));mountPersonalizationAnchor();if(window.Shopify?.designMode){document.addEventListener('shopify:section:load',event=>event.target.querySelectorAll('.ip-frameless-hero').forEach(hero=>mountFramelessHero(document,window,hero)));document.addEventListener('shopify:section:unload',event=>event.target.querySelectorAll('.ip-frameless-hero').forEach(hero=>mountedHeroes.get(hero)?.dispose()));}}
export function fitHero(hero,win){
  let pending,viewportWidth=win.innerWidth,viewportHeight=win.innerHeight;
  const fit=()=>{win.cancelAnimationFrame(pending);pending=win.requestAnimationFrame(()=>{
    const style=win.getComputedStyle(hero),mobile=win.innerWidth<=700;
    // Safari toolbar/keyboard height changes must not resize the page underneath a
    // gesture. Remeasure on actual width/orientation changes; desktop stays fluid.
    const touchViewport=mobile||win.matchMedia?.('(pointer:coarse)').matches;
    if(win.innerWidth!==viewportWidth||!touchViewport){viewportWidth=win.innerWidth;viewportHeight=win.innerHeight;}
    const copy=hero.querySelector('.copy'),actions=hero.querySelector('.mobile-actions'),demo=hero.querySelector('.demo');
    const width=demoWidth({viewportWidth:win.innerWidth,viewportHeight,heroTop:hero.getBoundingClientRect().top+win.scrollY,copyHeight:copy.getBoundingClientRect().height,actionsHeight:actions.getBoundingClientRect().height,chromeHeight:parseFloat(style.paddingTop)+parseFloat(style.paddingBottom)+parseFloat(style.rowGap)*2,columnWidth:mobile?hero.clientWidth-parseFloat(style.paddingLeft)-parseFloat(style.paddingRight):demo.parentElement.clientWidth});
    hero.style.setProperty('--ip-demo-width',`${Math.floor(width)}px`);
  });};
  const observer=new win.ResizeObserver(fit);
  for(const element of [hero.querySelector('.copy'),hero.querySelector('.mobile-actions'),win.document.querySelector('#header-group')])if(element)observer.observe(element);
  let active=true;const refresh=()=>{if(active)fit();};
  win.addEventListener('resize',refresh);win.addEventListener('pageshow',refresh);win.document.fonts?.ready.then(refresh);fit();
  return ()=>{active=false;win.cancelAnimationFrame(pending);observer.disconnect();win.removeEventListener?.('resize',refresh);win.removeEventListener?.('pageshow',refresh);};
}

export function mountPersonalizationAnchor(doc=document,win=window,loadStep=url=>import(url)){
 const target=doc.getElementById('personalize'),hero=doc.querySelector('.ip-frameless-hero');
 if(!target||!hero?.dataset.stepNavigation)return;
 const advance=()=>{if(win.location.hash!=='#personalize')return;loadStep(hero.dataset.stepNavigation).then(({scrollToStep})=>{if(win.location.hash==='#personalize')scrollToStep({win,target});}).catch(()=>target.scrollIntoView({block:'start'}));};
 win.addEventListener('hashchange',advance);
 if(win.location.hash==='#personalize')win.requestAnimationFrame(advance);
}
